import type { ClientIntent, GameId } from '@ringmaster/shared';

/**
 * "Call in the troupe" — the ONE definition of the AI-seat fill action, shared
 * by the phone and desktop lobbies (never two near-copies; the copies drift).
 *
 * AI seats are Blood Moon only: the heuristic driver knows one game, and the
 * server refuses `ADD_AI_SEAT` anywhere else. But the Discord Activity's room is
 * born One Night, so a lobby that stalls at one or two players is usually NOT a
 * Blood Moon room — offering the fill only inside Blood Moon would hide it from
 * most of the tables it exists for. So the tap says what it does: from another
 * game it switches the room to Blood Moon FIRST and then fills. Both intents are
 * host-gated and LOBBY-legal, they travel the same socket in order, and the
 * server decides how many seats "fill" actually means, so the client can never
 * race the arithmetic.
 *
 * Returns null when there is nothing to offer (a full Blood Moon table).
 */
export function troupeCall(
  gameId: GameId,
  playerCount: number,
  minPlayers: number,
  send: (intent: ClientIntent) => void,
): { label: string; onCall: () => void } | null {
  const isBloodMoon = gameId === 'bloodmoon';
  // Blood Moon's own floor is what the bots fill to, whichever game we start in.
  if (isBloodMoon && playerCount >= minPlayers) return null;
  return {
    // The owner's words, on PR #45: plain-spoken, not in-world. A stuck host
    // scanning a stalled lobby has to recognise the offer instantly, and
    // "the troupe" only means something to someone who already plays.
    // Both labels are one line at every width we ship — the long form
    // ("Fill in with AI opponents — Blood Moon") wrapped mid-phrase inside the
    // pill, which is the opposite of the tightening this row was asked for.
    label: isBloodMoon ? 'Fill in with AI opponents' : 'Play Blood Moon with AI',
    onCall: () => {
      if (!isBloodMoon) send({ type: 'SWITCH_GAME', gameId: 'bloodmoon' });
      send({ type: 'ADD_AI_SEAT' });
    },
  };
}
